import React, { useCallback, useState } from "react";
import { format } from "date-fns";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import {
    ENTERPRISE_TRIAL_CALL_URL,
    FRESHBOOKS_TRIAL_CALL_URL,
} from "@common/billing";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useEffectOnce } from "@hooks/useEffectOnce";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useScope } from "@state/scopeContext.lib";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { useUpgradeToCatalogV3Mutation } from "@api/flatRateBilling.api";
import { BookCallButton } from "@design-system/BookCall/BookCallButton";
import { useUpdateUserMutation } from "@api/user.api";
import { trackEvent } from "@lib/analytics";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Section } from "@design-system/Section/Section";
import { CalendarIcon } from "@icons";
import { usePlanManagement } from "../../billing/PlanManagement/PlanManagementContext";
import { Plans } from "../../billing/Plans/Plans";
import { FAQSection } from "./FAQSection";
import styles from "./CatalogV2UpgradePrompt.module.scss";

interface CatalogV3UpgradePromptParams {
    currentPlanName: string;
    workspaceId?: string | null;
    onUpdated?: () => void;
    nextInvoicePreview?: string | null;
}

interface CatalogV3Confirmation {
    action: "upgrade" | "downgrade";
    planName: string;
}

const useCatalogV3UpgradePrompt = ({
    currentPlanName,
    workspaceId,
    onUpdated,
    nextInvoicePreview,
}: CatalogV3UpgradePromptParams) => {
    const { error: toastError, toast } = useToaster();

    const upgradeToCatalogV3 = useUpgradeToCatalogV3Mutation(
        workspaceId ?? "",
        () => {
            // Phase 3: surface the V3 landing as a confirmation toast so
            // the user sees acknowledgement that they're now on the new
            // pricing even before the page re-renders with the
            // `licensedPriceItems` view.
            toast("You're now on the new plan.");
            onUpdated?.();
        },
        () => {
            toastError("Failed to migrate plan. Please try again.");
        },
    );

    const applyCatalogV3Update = useCallback(
        () => upgradeToCatalogV3.mutateAsync().catch(() => undefined),
        [upgradeToCatalogV3],
    );

    const renderCatalogV3UpgradePrompt = useCallback(
        () => (
            <div className={styles.updatePlanSection}>
                Your current {currentPlanName} plan is no longer available. You
                can continue using it or update to the new pricing.
                {nextInvoicePreview ? <> {nextInvoicePreview}</> : null}
            </div>
        ),
        [currentPlanName, nextInvoicePreview],
    );

    return {
        isUpdatingToCatalogV3: upgradeToCatalogV3.isPending,
        applyCatalogV3Update,
        renderCatalogV3UpgradePrompt,
    };
};

export const PlansPage: React.FC = () => {
    const { downgrade, upgrade } = usePlanManagement();
    const {
        plans,
        currentPlan,
        nextPlan,
        periodEnd,
        trialAvailable,
        isTrialing,
        isSubscribed,
        isLegacyPlan,
        bookedEnterpriseCall,
    } = useBillingStatus();
    // Phase 3: V3 routing flag drives the legacy "Update plan" CTA.
    const { billingCatalogV3 } = useTypedFlags();
    const { workspaceId } = useScope();
    const updateUser = useUpdateUserMutation();
    const [catalogV3Confirmation, setCatalogV3Confirmation] =
        useState<CatalogV3Confirmation | null>(null);
    const shouldShowUpgradeToCatalogV3 =
        !!workspaceId && billingCatalogV3 && isLegacyPlan && isSubscribed;
    const currentPlanName = flatRatePlanNames[currentPlan];
    const closeCatalogV3Confirmation = useCallback(() => {
        setCatalogV3Confirmation(null);
    }, []);

    const {
        isUpdatingToCatalogV3,
        applyCatalogV3Update,
        renderCatalogV3UpgradePrompt,
    } = useCatalogV3UpgradePrompt({
        currentPlanName,
        workspaceId,
        onUpdated: closeCatalogV3Confirmation,
    });

    useEffectOnce(() => {
        void trackEvent(AnalyticsEvent.VIEW_PLANS_TAB);
    });

    const handleCallBooked = useCallback(() => {
        updateUser.mutate({ bookedEnterpriseCall: true });
    }, [updateUser]);

    const inactiveEnterpriseAction = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            let inactiveEnterpriseCta: React.ReactNode;
            const isFreshbooksPlan = plan === FLAT_RATE_PLANS.FRESHBOOKS;

            if (bookedEnterpriseCall) {
                inactiveEnterpriseCta = <>Call booked</>;
            } else {
                const showCalendarIcon = !isFreshbooksPlan;
                const ctaText =
                    trialAvailable && !isFreshbooksPlan
                        ? "Request trial"
                        : "Book a call";

                inactiveEnterpriseCta = (
                    <>
                        {showCalendarIcon && (
                            <CalendarIcon className="icon-size-sm mr-1" />
                        )}
                        {ctaText}
                    </>
                );
            }

            return (
                <BookCallButton
                    variant={
                        bookedEnterpriseCall || isFreshbooksPlan
                            ? "primary"
                            : "tertiary"
                    }
                    url={
                        isFreshbooksPlan
                            ? FRESHBOOKS_TRIAL_CALL_URL
                            : ENTERPRISE_TRIAL_CALL_URL
                    }
                    disabled={bookedEnterpriseCall}
                    onBooked={handleCallBooked}
                    data-testid="book-call-button"
                >
                    {inactiveEnterpriseCta}
                </BookCallButton>
            );
        },
        [bookedEnterpriseCall, handleCallBooked, trialAvailable],
    );

    const renderUnsubscribedUserAction = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            if (plan === FLAT_RATE_PLANS.FREE) {
                return (
                    <Button
                        variant="primary"
                        size="md"
                        data-testid="active-plan-button"
                        disabled
                    >
                        Active
                    </Button>
                );
            } else if (
                [
                    FLAT_RATE_PLANS.ENTERPRISE,
                    FLAT_RATE_PLANS.FRESHBOOKS,
                ].includes(plan)
            ) {
                return inactiveEnterpriseAction(plan);
            } else {
                return (
                    <Button
                        variant="tertiary"
                        size="md"
                        onClick={() => upgrade(plan, trialAvailable)}
                        data-testid={
                            trialAvailable
                                ? `start-trial-${plan.toLowerCase()}-button`
                                : `upgrade-${plan.toLowerCase()}-button`
                        }
                    >
                        {trialAvailable ? "Start Free" : "Upgrade"}
                    </Button>
                );
            }
        },
        [inactiveEnterpriseAction, trialAvailable, upgrade],
    );

    const handleSubscribedUpgrade = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            if (shouldShowUpgradeToCatalogV3) {
                setCatalogV3Confirmation({
                    action: "upgrade",
                    planName: flatRatePlanNames[plan],
                });
                return;
            }

            upgrade(plan, isTrialing);
        },
        [isTrialing, shouldShowUpgradeToCatalogV3, upgrade],
    );

    const handleSubscribedDowngrade = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            if (shouldShowUpgradeToCatalogV3) {
                setCatalogV3Confirmation({
                    action: "downgrade",
                    planName: flatRatePlanNames[plan],
                });
                return;
            }

            downgrade(plan);
        },
        [downgrade, shouldShowUpgradeToCatalogV3],
    );

    const renderSubscribedUserAction = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            if (
                [
                    FLAT_RATE_PLANS.ENTERPRISE,
                    FLAT_RATE_PLANS.FRESHBOOKS,
                ].includes(currentPlan)
            ) {
                return null;
            } else if (plan === currentPlan) {
                if (shouldShowUpgradeToCatalogV3 && currentPlan === nextPlan) {
                    return (
                        <ButtonWithLoader
                            variant="tertiary"
                            size="md"
                            loading={isUpdatingToCatalogV3}
                            onClick={() => {
                                void applyCatalogV3Update();
                            }}
                            data-testid="update-plan-button"
                        >
                            Update plan
                        </ButtonWithLoader>
                    );
                }

                return (
                    <Button
                        variant="primary"
                        size="md"
                        disabled
                        data-testid="until-period-end-button"
                    >
                        {currentPlan !== nextPlan ? (
                            <>
                                Until{" "}
                                {format(periodEnd!, fullDateFullYearFormat)}
                            </>
                        ) : (
                            "Active"
                        )}
                    </Button>
                );
            } else if (
                [
                    FLAT_RATE_PLANS.ENTERPRISE,
                    FLAT_RATE_PLANS.FRESHBOOKS,
                ].includes(plan)
            ) {
                return inactiveEnterpriseAction(plan);
            } else if (
                plans.findIndex((p) => p.plan === plan) >
                plans.findIndex((p) => p.plan === currentPlan)
            ) {
                return (
                    <Button
                        variant="tertiary"
                        size="md"
                        onClick={() => handleSubscribedUpgrade(plan)}
                        data-testid={`upgrade-${plan.toLowerCase()}-button`}
                    >
                        Upgrade
                    </Button>
                );
            } else if (plan === nextPlan) {
                return (
                    <Button
                        variant="primary"
                        size="md"
                        disabled
                        data-testid="after-period-end-button"
                    >
                        After {format(periodEnd!, fullDateFullYearFormat)}
                    </Button>
                );
            } else {
                return (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => handleSubscribedDowngrade(plan)}
                        data-testid={`downgrade-${plan.toLowerCase()}-button`}
                    >
                        Downgrade
                    </Button>
                );
            }
        },
        [
            currentPlan,
            handleSubscribedDowngrade,
            handleSubscribedUpgrade,
            inactiveEnterpriseAction,
            nextPlan,
            periodEnd,
            plans,
            shouldShowUpgradeToCatalogV3,
            applyCatalogV3Update,
            isUpdatingToCatalogV3,
        ],
    );

    const renderAction = useCallback(
        (plan: FLAT_RATE_PLANS) =>
            currentPlan === FLAT_RATE_PLANS.FREE
                ? renderUnsubscribedUserAction(plan)
                : renderSubscribedUserAction(plan),
        [currentPlan, renderSubscribedUserAction, renderUnsubscribedUserAction],
    );

    return (
        <div>
            <ConfirmationModal
                show={!!catalogV3Confirmation}
                onHide={closeCatalogV3Confirmation}
                title={
                    <>
                        To {catalogV3Confirmation?.action}, please first update
                        to the new {currentPlanName} plan
                    </>
                }
                question={
                    <>
                        Your current {currentPlanName} plan is no longer
                        available. To {catalogV3Confirmation?.action} to{" "}
                        {catalogV3Confirmation?.planName}, you must be on the
                        current {currentPlanName} plan.
                    </>
                }
                yes="Update plan"
                yesBtnVariant="primary"
                onConfirm={applyCatalogV3Update}
            />

            <header className="settings__header">
                <h3>Plans</h3>
            </header>

            <Section
                heading="All plans"
                subheading={
                    <>
                        Need help deciding which plan is best for you?{" "}
                        <BookCallButton
                            variant="link"
                            size="sm"
                            style={{ display: "inline-flex" }}
                            url={ENTERPRISE_TRIAL_CALL_URL}
                            data-testid="book-call-button"
                        >
                            Book a call
                        </BookCallButton>
                    </>
                }
            >
                {shouldShowUpgradeToCatalogV3 && renderCatalogV3UpgradePrompt()}
                <Plans plans={plans} renderAction={renderAction} />
            </Section>

            <FAQSection onCallBooked={handleCallBooked} />
        </div>
    );
};
